/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Button from '@mui/material/Button';
import appTheme from '../../common/theme/appTheme.json';

interface FormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  title: string;
  closeText?: string;
  submitText: string;
  hideCloseButton?: boolean;

  submitDisabled?: boolean;
  children: React.ReactNode;
}

function FormModal({
  open,
  title,
  onClose,
  onSubmit,
  closeText,
  submitText,
  hideCloseButton,
  submitDisabled,
  children,
}: FormModalProps) {
  return (
    <Modal open={open} onClose={onClose} aria-labelledby={`modal-title`}>
      <Box
        sx={{
          position: 'absolute' as 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          backgroundColor: appTheme.primaryBackgroundColor,
          borderRadius: '8px',
          boxShadow: '0px 1px 4px 0px #0000002E',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            padding: '24px',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            id="modal-title"
            variant="h6"
            component="h2"
            sx={{
              fontFamily: appTheme.viasatFontFamilyRegular,
              fontStyle: 'normal',
              fontWeight: 800,
              fontSize: '20px',
              lineHeight: '28px',
              color: appTheme.primaryTextColor,
            }}
          >
            {title}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        <Box>{children}</Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            padding: '24px',
            justifyContent: 'flex-end',
            alignItems: 'center',
            borderTop: '1px solid #111920',
          }}
        >
          {!hideCloseButton && (
            <Button
              color="primary"
              variant="outlined"
              sx={{
                width: '31%',
                fontSize: appTheme.buttonFontSize,
                fontWeight: 500,
                color: '#ffffff',
                borderRadius: '18px',
                borderColor: appTheme.primaryTextColor,
                backgroundColor: appTheme.mptPrimaryBackgroundColor,
                '&:hover': {
                  backgroundColor: appTheme.mptPrimaryBackgroundColor,
                  borderColor: appTheme.primaryTextColor,
                },
                marginRight: '2%',
              }}
              onClick={onClose}
            >
              {closeText}
            </Button>
          )}

          <Button
            color="primary"
            type="submit"
            id="form-modal-submit-button"
            sx={{
              minWidth: '31%',
              fontSize: appTheme.buttonFontSize,
              fontWeight: 500,
              borderRadius: '18px',
              backgroundColor: appTheme.mptLightBlue,
              color: appTheme.darkGrayTextColor,
              marginLeft: '2%',
              '&:hover': { backgroundColor: appTheme.mptLightBlue },
            }}
            disabled={submitDisabled}
            onClick={() => {
              onSubmit();
              onClose();
            }}
          >
            {submitText}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default FormModal;
